// AI API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = `${getApiBaseUrl()}/api`;

// Create axios instance with default config and auth error handling
// Note: aiApi uses /api prefix in baseURL
const apiClient = createApiClient(API_BASE_URL);

// Types
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ChatRequest {
  message: string;
  conversationHistory?: ChatMessage[];
}

export interface ChatResponse {
  response: string;
  conversationId?: string;
}

export interface HeadlineGenerationRequest {
  content: string;
  tone?: string;
  count?: number;
}

export interface HeadlineGenerationResponse {
  headlines: string[];
  suggestions?: string[];
}

export interface ContentAnalysisRequest {
  content: string;
  prId?: string;
}

export interface ContentAnalysisResponse {
  sentiment: string;
  readabilityScore: number;
  seoScore: number;
  suggestions: string[];
  keywords: string[];
  improvements: {
    category: string;
    suggestion: string;
    priority: 'high' | 'medium' | 'low';
  }[];
}

export interface OptimalTimingRequest {
  industry: string;
  targetAudience?: string[];
  contentType?: string;
}

export interface OptimalTimingResponse {
  recommendations: {
    dateTime: string;
    reason: string;
    confidence: number;
  }[];
}

export interface ChannelRecommendationRequest {
  content: string;
  industry: string;
  geography?: string[];
  budget?: number;
}

export interface ChannelRecommendationResponse {
  recommendations: {
    channel: string;
    relevance: number;
    reason: string;
    estimatedReach: number;
  }[];
}

export interface CrisisDetectionRequest {
  keywords?: string[];
  companyName: string;
}

export interface CrisisDetectionResponse {
  riskLevel: 'low' | 'medium' | 'high';
  sentiment: string;
  mentions: number;
  alerts: {
    type: string;
    severity: string;
    message: string;
  }[];
}

// AI API Functions

/**
 * Send chat message to AI assistant
 */
export const sendChatMessage = async (
  request: ChatRequest
): Promise<ChatResponse> => {
  try {
    const response = await apiClient.post('/ai/chat', request);
    return response.data;
  } catch (error: any) {
    console.error('Error sending chat message:', error);
    throw new Error(error.response?.data?.message || 'Failed to send message');
  }
};

/**
 * Generate AI-powered headlines
 */
export const generateHeadlines = async (
  request: HeadlineGenerationRequest
): Promise<HeadlineGenerationResponse> => {
  try {
    const response = await apiClient.post('/ai/generate-headlines', request);
    return response.data;
  } catch (error: any) {
    console.error('Error generating headlines:', error);
    throw new Error(error.response?.data?.message || 'Failed to generate headlines');
  }
};

/**
 * Analyze content with AI
 */
export const analyzeContent = async (
  request: ContentAnalysisRequest
): Promise<ContentAnalysisResponse> => {
  try {
    const response = await apiClient.post('/ai/analyze-content', request);
    return response.data;
  } catch (error: any) {
    console.error('Error analyzing content:', error);
    throw new Error(error.response?.data?.message || 'Failed to analyze content');
  }
};

/**
 * Get optimal timing recommendations
 */
export const getOptimalTiming = async (
  request: OptimalTimingRequest
): Promise<OptimalTimingResponse> => {
  try {
    const response = await apiClient.post('/ai/optimal-timing', request);
    return response.data;
  } catch (error: any) {
    console.error('Error getting optimal timing:', error);
    throw new Error(error.response?.data?.message || 'Failed to get timing recommendations');
  }
};

/**
 * Get channel recommendations
 */
export const getChannelRecommendations = async (
  request: ChannelRecommendationRequest
): Promise<ChannelRecommendationResponse> => {
  try {
    const response = await apiClient.post('/ai/channel-recommendations', request);
    return response.data;
  } catch (error: any) {
    console.error('Error getting channel recommendations:', error);
    throw new Error(error.response?.data?.message || 'Failed to get channel recommendations');
  }
};

/**
 * Detect crisis and monitor sentiment
 */
export const detectCrisis = async (
  request: CrisisDetectionRequest
): Promise<CrisisDetectionResponse> => {
  try {
    const response = await apiClient.post('/ai/crisis-detection', request);
    return response.data;
  } catch (error: any) {
    console.error('Error detecting crisis:', error);
    throw new Error(error.response?.data?.message || 'Failed to detect crisis');
  }
};

/**
 * Get content enhancement suggestions
 */
export const getContentSuggestions = async (content: string): Promise<string[]> => {
  try {
    const response = await apiClient.post('/ai/content-suggestions', { content });
    return response.data.suggestions || [];
  } catch (error: any) {
    console.error('Error getting content suggestions:', error);
    throw new Error(error.response?.data?.message || 'Failed to get suggestions');
  }
};

export default {
  sendChatMessage,
  generateHeadlines,
  analyzeContent,
  getOptimalTiming,
  getChannelRecommendations,
  detectCrisis,
  getContentSuggestions
};




















